const COUNTRY_CACHE_KEY = "newsletter-country-v1";
const COUNTRY_CACHE_TTL = 2 * 60 * 60 * 1000;
const TRACKING_FIELDS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];

const setFieldValue = (form, fieldName, value) => {
  const field = form.querySelector(`[name="${fieldName}"]`);

  if (field) {
    field.value = value;
  }
};

const getFormValue = (form, names) => {
  for (let index = 0; index < names.length; index += 1) {
    const field = form.querySelector(`[name="${names[index]}"]`);

    if (field && field.value.trim()) {
      return field.value.trim();
    }
  }

  return "";
};

const getSubmittedValue = (inputs, names) => {
  if (!Array.isArray(inputs)) {
    return "";
  }

  const input = inputs.find((item) => names.includes(item.name) && item.value);

  return input ? String(input.value).trim() : "";
};

const renderTextTemplate = (element, template, placeholder, value) => {
  element.innerHTML = template;

  const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
  let textNode = walker.nextNode();

  while (textNode) {
    textNode.nodeValue = textNode.nodeValue.replace(placeholder, value);
    textNode = walker.nextNode();
  }
};

const getCachedCountry = () => {
  try {
    const cached = JSON.parse(localStorage.getItem(COUNTRY_CACHE_KEY));
    const savedAt = cached ? Number(cached.savedAt) : 0;

    if (
      !cached ||
      !cached.country ||
      !Number.isFinite(savedAt) ||
      savedAt > Date.now() ||
      Date.now() - savedAt >= COUNTRY_CACHE_TTL
    ) {
      localStorage.removeItem(COUNTRY_CACHE_KEY);
      return "";
    }

    return cached.country;
  } catch (error) {
    return "";
  }
};

const cacheCountry = (country) => {
  try {
    localStorage.setItem(COUNTRY_CACHE_KEY, JSON.stringify({ country, savedAt: Date.now() }));
  } catch (error) {
    // Continue without caching when browser storage is unavailable.
  }
};

const populateCountry = (form) => {
  const countryField = form.querySelector('[name="country"]');

  if (!countryField) {
    return;
  }

  const cachedCountry = getCachedCountry();
  if (cachedCountry) {
    countryField.value = cachedCountry;
    return;
  }

  if (!window.xometryNewsletterForm || !window.xometryNewsletterForm.ajaxUrl) {
    return;
  }

  const requestData = new FormData();
  requestData.append("action", "get_user_country");

  fetch(window.xometryNewsletterForm.ajaxUrl, {
    method: "POST",
    body: requestData,
    credentials: "same-origin",
  })
    .then((response) => {
      if (!response.ok) {
        throw new Error("Country request failed.");
      }

      return response.json();
    })
    .then((response) => {
      const country = response.success && response.data ? response.data.country : "";

      if (country) {
        cacheCountry(country);
        countryField.value = country;
      }
    })
    .catch(() => {
      // A missing country must not prevent form submission.
    });
};

const populateBlock = (block) => {
  const params = new URLSearchParams(window.location.search);
  const form = block.querySelector(".wpcf7-form");
  const eventName = params.get("event") || "";
  const offer = params.get("offer") || block.dataset.defaultOffer || "";
  const hasOffer = Boolean(params.get("offer"));
  const couponOnly = (params.get("coupon_only") || "").toLowerCase() === "true";
  const eventLabel = block.querySelector("[data-event-promo-event]");
  const offerElement = block.querySelector("[data-event-promo-offer]");

  block.dataset.showRewardReminder = hasOffer && !couponOnly ? "true" : "false";

  if (eventLabel && eventName) {
    eventLabel.textContent = eventName;
    eventLabel.hidden = false;
  }

  if (offerElement) {
    offerElement.textContent = offer;
  }

  if (!form) {
    return;
  }

  TRACKING_FIELDS.forEach((fieldName) => {
    setFieldValue(form, fieldName, params.get(fieldName) || "");
  });

  setFieldValue(form, "page_url", window.location.href);
  setFieldValue(form, "event", eventName);
  setFieldValue(form, "offer", offer);
  setFieldValue(form, "value", params.get("value") || "");
  setFieldValue(form, "coupon", params.get("coupon") || "WELCOME");
  setFieldValue(form, "expiry", params.get("expiry") || "");
  populateCountry(form);
};

const showThanks = (form, submittedInputs) => {
  const block = form.closest(".event-promo");

  if (!block) {
    return;
  }

  const thanks = block.querySelector(".event-promo__thanks");
  const formWrap = block.querySelector(".event-promo__form-wrap");
  const title = block.querySelector("[data-event-promo-thank-title]");
  const subline = block.querySelector("[data-event-promo-thank-subline]");
  const reminder = block.querySelector("[data-event-promo-reward-reminder]");
  const firstNameFields = ["first_name", "first-name", "firstname"];
  const lastNameFields = ["last_name", "last-name", "lastname"];
  const fullNameFields = ["full_name", "full-name", "your-name"];
  const emailFields = ["email", "your-email"];
  const firstName =
    getSubmittedValue(submittedInputs, firstNameFields) || getFormValue(form, firstNameFields);
  const lastName =
    getSubmittedValue(submittedInputs, lastNameFields) || getFormValue(form, lastNameFields);
  const fullName =
    getSubmittedValue(submittedInputs, fullNameFields) ||
    getFormValue(form, fullNameFields) ||
    [firstName, lastName].filter(Boolean).join(" ");
  const email =
    getSubmittedValue(submittedInputs, emailFields) || getFormValue(form, emailFields);

  if (!thanks || !formWrap) {
    return;
  }

  if (!thanks.hidden) {
    return;
  }

  if (title) {
    title.textContent = thanks.dataset.thankTitle.replace("{full_name}", fullName);
  }

  if (subline) {
    renderTextTemplate(subline, thanks.dataset.thankSubline, "{email}", email);
  }

  if (reminder) {
    reminder.hidden = block.dataset.showRewardReminder !== "true";
  }

  formWrap.hidden = true;
  thanks.hidden = false;
  thanks.focus({ preventScroll: true });
};

const EventPromo = () => {
  const blocks = [...document.querySelectorAll(".event-promo")];

  if (!blocks.length) {
    return;
  }

  blocks.forEach(populateBlock);

  document.addEventListener("wpcf7mailsent", (event) => {
    const form = event.target.matches(".wpcf7-form")
      ? event.target
      : event.target.querySelector(".wpcf7-form");

    if (form) {
      showThanks(form, event.detail ? event.detail.inputs : []);
    }
  });
};

export default EventPromo;
